import React, { useState } from "react";
import { Button, Col, Form, Row, Spinner, Alert } from "react-bootstrap";
import Dropzone from "react-dropzone";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { createRecentWork, getRecentWork } from "../../../../slices/thunk";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface AddRecentWorkProps {
  contractorId: any;
  handleSucces: (type: any) => void;
}

const AddRecentWork: React.FC<AddRecentWorkProps> = ({
  contractorId,
  handleSucces,
}) => {
  const dispatch: any = useDispatch();
  const { t } = useTranslation();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<any>(false);
  const [notification, setNotification] = useState<{
    status: boolean;
    message: string;
    variant: "success" | "danger" | "warning" | "info";
  } | null>(null);

  const handleDrop = (acceptedFiles: File[]) => {
    setSelectedFiles([...selectedFiles, ...acceptedFiles]);
  };

  const handleRemove = (index: number) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const filePreviews = selectedFiles.map((file, index) => (
    <div key={`${file.name}-${index}`} className="recent-work-file-preview">
      <div className="recent-work-file-thumb">
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
        />
      </div>
      <div className="min-w-0">
        <span>{file.name}</span>
        <small>{Math.max(file.size / 1024 / 1024, 0.01).toFixed(2)} MB</small>
      </div>
      <Button
        variant="soft-danger"
        size="sm"
        className="ms-auto"
        onClick={() => handleRemove(index)}
        aria-label={t("Remove")}
      >
        <i className="bi bi-x-lg"></i>
      </Button>
    </div>
  ));

  const formik = useFormik({
    initialValues: {
      recentWorkName: "",
      recentWorkDescription: "",
      recentWorkContId: contractorId,
      recentWorkvideo: null,
      startedAt: null as Date | null,
      completedAt:null as Date | null,
    },
    validationSchema: Yup.object({
      recentWorkName: Yup.string().required("Required"),
      recentWorkDescription: Yup.string().required("Required"),
      startedAt: Yup.string().required("Required"),
      completedAt: Yup.string().required("Required"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      const formData = {
        ...values,
        images: selectedFiles,
      };
      dispatch(createRecentWork(formData)).finally(() => {
        formik.resetForm();
        setSelectedFiles([]);
        setLoading(false);
        dispatch(getRecentWork());
        handleSucces(true);
        setNotification({
          status: true,
          message: "Recent Work Added Successfully",
          variant: "success",
        });
      });
    },
  });

  const handleAlertClick = () => {
    setNotification(null);
  };

  return (
    <React.Fragment>
      {notification && notification.status && (
        <div
          style={{
            position: "absolute",
            top: "5%",
            right: "24px",
            zIndex: 99999,
          }}
        >
          <Alert variant={notification.variant} onClick={handleAlertClick}>
            {t(notification.message) }
          </Alert>
        </div>
      )}
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "200px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("Loading...")} </span>
          </Spinner>
        </div>
      ) : (
        <Col lg={12}>
          <Form className="recent-work-form" onSubmit={formik.handleSubmit}>
            <div className="recent-work-form-section">
              <div className="recent-work-section-heading">
                <div>
                  <h6>{t("Project Media")}</h6>
                  <p>{t("Upload clear photos that show the completed work.")}</p>
                </div>
                <span className="recent-work-selected-count">{selectedFiles.length} {t("Selected")}</span>
              </div>
              <Dropzone onDrop={handleDrop} accept={{ "image/*": [] }}>
                {({ getRootProps, getInputProps }: any) => (
                  <div {...getRootProps({ className: "recent-work-dropzone" })}>
                    <input {...getInputProps()} />
                    <div className="recent-work-dropzone-content">
                      <div className="recent-work-upload-icon">
                        <i className="bi bi-cloud-arrow-up"></i>
                      </div>
                      <strong>{t("Drop project photos here")} <span className="text-danger">*</span></strong>
                      <p>{t("PNG, JPG, or JPEG photos work best. Click to browse.")}</p>
                    </div>
                  </div>
                )}
              </Dropzone>
              {selectedFiles.length > 0 && (
                <div className="recent-work-file-list">
                  {filePreviews}
                </div>
              )}
            </div>

            <div className="recent-work-form-section">
              <div className="recent-work-section-heading">
                <div>
                  <h6>{t("Project Details")}</h6>
                  <p>{t("Keep the title specific and the description homeowner-friendly.")}</p>
                </div>
              </div>
            <Row className="g-3">
              <Col lg={12}>
                <div>
                  <Form.Label htmlFor="recentWorkName">
                 {t("Project Title")}   <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id="recentWorkName"
                    name="recentWorkName"
                    placeholder="Example: Kitchen remodel in Austin"
                    value={formik.values.recentWorkName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.recentWorkName &&
                      formik.touched.recentWorkName
                    }
                  />
                  {formik.errors.recentWorkName &&
                    formik.touched.recentWorkName && (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.recentWorkName}
                      </Form.Control.Feedback>
                    )}
                </div>
              </Col>
              <Col lg={12}>
                <div>
                  <Form.Label htmlFor="recentWorkDescription">
                 {t("Project Description")}   
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    id="recentWorkDescription"
                    name="recentWorkDescription"
                    placeholder="Describe what was completed, materials used, and any notable outcome."
                    rows={4}
                    value={formik.values.recentWorkDescription}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.recentWorkDescription &&
                      formik.touched.recentWorkDescription
                    }
                  />
                  {formik.errors.recentWorkDescription &&
                    formik.touched.recentWorkDescription && (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.recentWorkDescription}
                      </Form.Control.Feedback>
                    )}
                </div>
              </Col>
              <Col lg={6}>
                <div>
                  <Form.Label htmlFor="startedAt">
                {t("Started At")}    <span className="text-danger">*</span>
                  </Form.Label>
                  <DatePicker
                    id="startedAt"
                    selected={formik.values.startedAt}
                    onChange={(date: Date | null) => formik.setFieldValue("startedAt", date)}
                    onBlur={() => formik.setFieldTouched("startedAt", true)}
                    className={`form-control ${formik.touched.startedAt && formik.errors.startedAt ? "is-invalid" : ""}`}
                    dateFormat="dd MMM, yyyy"
                    placeholderText={t("Select date")}
                  />
                  {formik.errors.startedAt && formik.touched.startedAt && (
                    <div className="text-danger">{formik.errors.startedAt}</div>
                  )}
                </div>
              </Col>
              <Col lg={6}>
              <div className="position-relative">
  <Form.Label htmlFor="completedAt">
    {t("Completed At")} <span className="text-danger">*</span>
  </Form.Label>

  <DatePicker
    id="completedAt"
    selected={formik.values.completedAt}
    onChange={(date: Date | null) => formik.setFieldValue("completedAt", date)}
    onBlur={() => formik.setFieldTouched("completedAt", true)}
    dateFormat="dd MMM, yyyy"
    placeholderText={t("Select date")}
    className={`form-control ${formik.touched.completedAt && formik.errors.completedAt ? "is-invalid" : ""}`}
  />

  {formik.errors.completedAt && formik.touched.completedAt && (
    <Form.Control.Feedback type="invalid" className="d-block">
      {formik.errors.completedAt}
    </Form.Control.Feedback>
  )}
</div>

              </Col>
            </Row>
            </div>
            <div className="recent-work-form-actions">
              <span className="recent-work-form-hint">
                {t("Photos and details will appear in your public profile.")}
              </span>
              <Button variant="primary" type="submit" className="px-4">
                <i className="bi bi-check2-circle align-baseline me-1"></i>
                {t("Save Work")}
              </Button>
            </div>
          </Form>
        </Col>
      )}
    </React.Fragment>
  );
};

export default AddRecentWork;
